/**
 * useBatchCovers – fetch cover images for multiple entities in ONE query.
 * Used by Explore to avoid N+1 queries per card.
 *
 * Returns a Map<ownerId, url> for the requested variant.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset, OwnerType, VariantWidth } from '@/lib/media/mediaTypes';

export function useBatchCovers(
  ownerType: OwnerType,
  ownerIds: string[],
  variant: VariantWidth = 320,
) {
  const stableKey = ownerIds.slice().sort().join(',');

  const { data: coverMap = new Map<string, string>(), isLoading } = useQuery({
    queryKey: ['batch-covers', ownerType, stableKey, variant],
    queryFn: async () => {
      if (ownerIds.length === 0) return new Map<string, string>();

      const { data, error } = await supabase
        .from('media_assets')
        .select('owner_id, bucket, path_320, path_640, path_1280')
        .eq('owner_type', ownerType)
        .eq('kind', 'cover')
        .in('owner_id', ownerIds)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const result = new Map<string, string>();
      for (const row of (data ?? []) as unknown as MediaAsset[]) {
        if (row.owner_id && !result.has(row.owner_id)) {
          const url = resolveAssetUrl(row, variant);
          if (url) result.set(row.owner_id, url);
        }
      }
      return result;
    },
    enabled: ownerIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return { coverMap, isLoading };
}
